import qs from 'qs';
import type { ReactElement } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Layout, NotFound } from '../components';
import { RouteHistoryProvider, Store } from '../store';
import { Account } from './account';
import { AssetDetails } from './asset-details';
import { ConfirmForgotPassword } from './confirm-forgot-password';
import { Customers } from './customers';
import { Encoders } from './encoders';
import { EventDetails } from './event-details';
import { EventDetailsV2 } from './event-details-v2';
import { Events } from './events';
import { ForgotPassword } from './forgot-password';
import { Installers } from './installers';
import { LiveLite, LiveLiteEvent } from './live-lite';
import { Login } from './login';
import { Logout } from './logout';
import { Manager } from './manager';
import { Operators } from './operators';
import { UserConfirmation } from './user-confirmation';
// import { Users } from './users';

/* Prefix of the pages without sidebar. */
const LITE_ROUTE = '/lite';

const AuthGuard = (): ReactElement => {
  const { me, token } = Store.User.useIdentity();
  const { pathname } = useLocation();

  const { device_access, event_access, installer_management } = me?.commandCenterAccess ?? {};
  const hasAnyAccess = !!device_access || !!event_access || !!installer_management;

  /*
   * If there is no authentication token redirect to the `/login` route.
   */
  if (!token || !me || !hasAnyAccess) {
    const queryParams = qs.stringify({
      redirect: `${location.pathname}${location.search}${location.hash}`
    });

    return <Redirect to={`/login?${queryParams}`} />;
  }

  /*
   * Standalone pages: same auth and header, no sidebar, and nothing in the app links to them — they are
   * reached by typing the URL.
   */
  if (me.commandCenterAccess?.event_access && pathname.startsWith(`${LITE_ROUTE}/`)) {
    return (
      <Layout sidebar={false}>
        <Switch>
          {/* `live` before `:eid`, or the list would be read as an event id. */}
          <Route path={`${LITE_ROUTE}/live`} component={LiveLite} />
          <Route path={`${LITE_ROUTE}/:eid`} component={LiveLiteEvent} />
          <Route component={NotFound} />
        </Switch>
      </Layout>
    );
  }

  return (
    /*
     * If there is no authentication token redirect to the `/login` route.
     */

    // Otherwise, build the frontend routes
    <Layout>
      <Switch>
        <Route path='/account' component={Account} />
        {me.commandCenterAccess?.event_access && (
          <Route path='/events/:slug?' component={Events} />
        )}
        {me.commandCenterAccess?.event_access && (
          <Route path='/event-v2/:eid' component={EventDetails} />
        )}
        {me.commandCenterAccess?.event_access && (
          <Route path='/event/:eid' component={EventDetailsV2} />
        )}
        <Route path='/asset/:eid' component={AssetDetails} />
        <Route path='/customers' component={Customers} />
        {me.commandCenterAccess?.device_access && (
          <Route path='/manager' component={Manager} />
        )}
        {me.commandCenterAccess?.installer_management && (
          <Route path='/installers' component={Installers} />
        )}
        <Route path='/encoders' component={Encoders} />
        <Route path='/operators' component={Operators} />
        {/* <Route path='/users' component={Users} /> */}

        <Route exact path={['/', '/home']}>
          {me.commandCenterAccess?.event_access ? (
            <Redirect to='/events' />
          ) : me.commandCenterAccess?.device_access ? (
            <Redirect to='/manager' />
          ) : me.commandCenterAccess?.installer_management ? (
            <Redirect to='/installers' />
          ) : (
            null
          )}
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
};

export const Router = (): ReactElement => (
  <RouteHistoryProvider>
    <Switch>
      <Route path='/(login|signin)' component={Login} />
      <Route path='/forgot-password' component={ForgotPassword} />
      <Route path='/forgot-password-confirmation' component={ConfirmForgotPassword} />
      <Route path='/user-confirmation' component={UserConfirmation} />
      <Route path='/logout' component={Logout} />
      <Route component={AuthGuard} />
    </Switch>
  </RouteHistoryProvider>
);
